import { Switch, Route, useLocation, Redirect } from "wouter";
import { useEffect, useRef, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { queryClient } from "./lib/queryClient";
import "@/lib/apideck-vault-preload";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth";
import { EntityProvider } from "@/lib/entity-context";
import { FeaturesProvider } from "@/lib/features";
import { FeatureGate } from "@/components/feature-gate";
import { useProfileConfig } from "@/lib/use-profile-config";
import { trackPageView, trackEvent, incrementUserProfile, registerSuperProperties, setUserProfileProperties, setUserProfileOnce, resetSessionModules, wsLocalKey, captureAndRegisterUtms } from "@/lib/analytics";
import i18n from "@/i18n";
import { HeaderNav } from "@/components/header-nav";
import { BottomNav } from "@/components/bottom-nav";
import { CommandPalette } from "@/components/command-palette";
import { UpgradeModalProvider } from "@/hooks/use-upgrade-modal";
import { UpgradeModal } from "@/components/upgrade-modal";
import { Loader2 } from "lucide-react";
import { OctaLogo } from "@/components/octa-logo";
import { ErrorBoundary } from "@/components/error-boundary";
import { TrialBanner } from "@/components/trial-banner";
import { SupportModeBanner } from "@/components/support-mode-banner";
import { SyncBanner } from "@/components/sync-banner";
import { SupportModeProvider } from "@/lib/support-mode";
import { SyncActivityProvider } from "@/lib/sync-activity-context";
import { lazyRetry } from "@/lib/lazy-retry";
import type { Workspace } from "@shared/schema";

const NotFound = lazyRetry(() => import("@/pages/not-found"));
const AuthPage = lazyRetry(() => import("@/pages/auth-page"));
const Dashboard = lazyRetry(() => import("@/pages/dashboard"));
const APPage = lazyRetry(() => import("@/pages/ap/index"));
const BillFormPage = lazyRetry(() => import("@/pages/ap/bill-form"));
const BillNewPage = lazyRetry(() => import("@/pages/ap/bill-new"));
const BillCsvUploadPage = lazyRetry(() => import("@/pages/ap/bill-csv-upload"));
const BillSyncPage = lazyRetry(() => import("@/pages/ap/bill-sync"));
const BillScanUploadPage = lazyRetry(() => import("@/pages/ap/bill-scan-upload"));
const BillDetailPage = lazyRetry(() => import("@/pages/ap/bill-detail"));
const InboundEinvoicesPage = lazyRetry(() => import("@/pages/ap/inbound-einvoices"));
const InboundEinvoiceDetailPage = lazyRetry(() => import("@/pages/ap/inbound-einvoice-detail"));
const PoDetailPage = lazyRetry(() => import("@/pages/ap/po-detail"));
const ARPage = lazyRetry(() => import("@/pages/ar/index"));
const InvoiceFormPage = lazyRetry(() => import("@/pages/ar/invoice-form"));
const InvoiceNewPage = lazyRetry(() => import("@/pages/ar/invoice-new"));
const InvoiceCsvUploadPage = lazyRetry(() => import("@/pages/ar/invoice-csv-upload"));
const InvoiceBulkConvertPage = lazyRetry(() => import("@/pages/ar/invoice-bulk-convert"));
const EinvoiceBulkUploadPage = lazyRetry(() => import("@/pages/ar/einvoice-bulk-upload"));
const InvoiceSyncPage = lazyRetry(() => import("@/pages/ar/invoice-sync"));
const InvoiceDetailPage = lazyRetry(() => import("@/pages/ar/invoice-detail"));
const VendorDetailPage = lazyRetry(() => import("@/pages/ap/vendor-detail"));
const VendorFormPage = lazyRetry(() => import("@/pages/ap/vendor-form"));
const CustomerDetailPage = lazyRetry(() => import("@/pages/ar/customer-detail"));
const CustomerFormPage = lazyRetry(() => import("@/pages/ar/customer-form"));
const FinancingPage = lazyRetry(() => import("@/pages/financing/index"));
const ApplicationDetailPage = lazyRetry(() => import("@/pages/financing/application-detail"));
const FacilityDetailPage = lazyRetry(() => import("@/pages/financing/facility-detail"));
const SettingsPage = lazyRetry(() => import("@/pages/settings/layout"));
const TasksPage = lazyRetry(() => import("@/pages/tasks/index"));
const TaskFormPage = lazyRetry(() => import("@/pages/tasks/task-form"));
const ReportsPage = lazyRetry(() => import("@/pages/reports/index"));
const ReportDetailPage = lazyRetry(() => import("@/pages/reports/report-detail"));
const BudgetsPage = lazyRetry(() => import("@/pages/reports/budgets"));
const BudgetVsActualPage = lazyRetry(() => import("@/pages/reports/budget-vs-actual"));
const SchedulesPage = lazyRetry(() => import("@/pages/reports/schedules"));
const ReportBuilderPage = lazyRetry(() => import("@/pages/reports/report-builder"));
const CustomerPortalPage = lazyRetry(() => import("@/pages/customer-portal"));
const VendorPortalPage = lazyRetry(() => import("@/pages/vendor-portal"));
const PayRedirect = lazyRetry(() => import("@/pages/pay-redirect"));
const CheckoutRedirect = lazyRetry(() => import("@/pages/checkout-redirect"));
const AutomationBuilderPage = lazyRetry(() => import("@/pages/automations/builder"));
const AutomationsListPage = lazyRetry(() => import("@/pages/automations/index"));
const AutomationRunsPage = lazyRetry(() => import("@/pages/automations/runs"));
const AutomationHealthPage = lazyRetry(() => import("@/pages/automations/health"));
const AutomationApprovalsPage = lazyRetry(() => import("@/pages/automations/approvals"));
const AlBillingWizardPage = lazyRetry(() => import("@/pages/automations/al-billing-wizard"));
const PipelineInstanceDetailPage = lazyRetry(() => import("@/pages/automations/pipeline-instance-detail"));
const PipelineInstancesPage = lazyRetry(() => import("@/pages/automations/pipeline-instances"));
const InboxPage = lazyRetry(() => import("@/pages/inbox/index"));
const OnboardingPage = lazyRetry(() => import("@/pages/onboarding"));
const ChoosePlanPage = lazyRetry(() => import("@/pages/choose-plan"));
const CreditNotesPage = lazyRetry(() => import("@/pages/ar/credit-notes"));
const DealDetailPage = lazyRetry(() => import("@/pages/ar/deal-detail"));
const ContractDetailPage = lazyRetry(() => import("@/pages/ar/contract-workspace"));
const ContractRecordDetailPage = lazyRetry(() => import("@/pages/ar/contract-record-detail"));
const InvoiceDraftQueuePage = lazyRetry(() => import("@/pages/ar/invoice-draft-queue"));
const AdminPage = lazyRetry(() => import("@/pages/admin"));
const TaxRatesPage = lazyRetry(() => import("@/pages/shared/tax-rates"));
const OperationsPage = lazyRetry(() => import("@/pages/operations/index"));
const GrnDetailPage = lazyRetry(() => import("@/pages/ap/grn-detail"));
const ChatPage = lazyRetry(() => import("@/pages/chat/index"));
const CommunicationsPage = lazyRetry(() => import("@/pages/communications/index"));
const ActivatePage = lazyRetry(() => import("@/pages/activate"));
const UpgradeWelcomePage = lazyRetry(() => import("@/pages/upgrade-welcome"));
const DowngradeNoticePage = lazyRetry(() => import("@/pages/downgrade-notice"));
const SystemHealthPage = lazyRetry(() => import("@/pages/system-health"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <OctaLogo animate />
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}

function SyncRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/settings?section=integrations&sub=sync", { replace: true });
  }, [setLocation]);
  return null;
}

function RemindersRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/ar?tab=reminders", { replace: true });
  }, [setLocation]);
  return null;
}

function OperationsRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => {
    setLocation("/", { replace: true });
  }, [setLocation]);
  return null;
}


function GatedRoute({ feature, Component, params }: { feature: keyof import("@/lib/features").FeatureFlags; Component: React.ComponentType<any>; params?: Record<string, string | undefined> }) {
  const { data: profileConfig } = useProfileConfig();
  const profileState = profileConfig?.featureStates?.[feature];
  const upsell = profileConfig?.upsellPrompts?.find(p => p.featureKey === feature);

  return (
    <FeatureGate
      feature={feature}
      profileState={profileState}
      upgradeMessage={upsell?.message}
      tierRequired={upsell?.targetTier}
    >
      <Component params={params} />
    </FeatureGate>
  );
}

function usePageTracking() {
  const [location] = useLocation();
  const prevLocation = useRef(location);
  useEffect(() => {
    if (location !== prevLocation.current) {
      prevLocation.current = location;
      trackPageView();
    }
  }, [location]);
  useEffect(() => {
    trackPageView();
  }, []);
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Dashboard} />
        <Route path="/ap">{() => <GatedRoute feature="ap" Component={APPage} />}</Route>
        <Route path="/ap/bills/new/manual">{() => <GatedRoute feature="ap" Component={BillFormPage} />}</Route>
        <Route path="/ap/bills/new/csv">{() => <GatedRoute feature="ap" Component={BillCsvUploadPage} />}</Route>
        <Route path="/ap/bills/new/sync">{() => <GatedRoute feature="ap" Component={BillSyncPage} />}</Route>
        <Route path="/ap/bills/new/scan">{() => <GatedRoute feature="ap" Component={BillScanUploadPage} />}</Route>
        <Route path="/ap/bills/new">{() => <GatedRoute feature="ap" Component={BillNewPage} />}</Route>
        <Route path="/ap/bills/:id/edit">{(params) => <GatedRoute feature="ap" Component={BillFormPage} params={params} />}</Route>
        <Route path="/ap/vendors/new">{() => <GatedRoute feature="ap" Component={VendorFormPage} />}</Route>
        <Route path="/ap/vendors/:id">{(params) => <GatedRoute feature="ap" Component={VendorDetailPage} params={params} />}</Route>
        <Route path="/ap/grns/:id">{(params) => <GatedRoute feature="ap" Component={GrnDetailPage} params={params} />}</Route>
        <Route path="/ap/purchase-orders/:id">{(params) => <GatedRoute feature="purchaseOrders" Component={PoDetailPage} params={params} />}</Route>
        <Route path="/ap/bills/:id">{(params) => <GatedRoute feature="ap" Component={BillDetailPage} params={params} />}</Route>
        <Route path="/ap/inbound-einvoices/:id">{(params) => <GatedRoute feature="ap" Component={InboundEinvoiceDetailPage} params={params} />}</Route>
        <Route path="/ap/inbound-einvoices">{() => <GatedRoute feature="ap" Component={InboundEinvoicesPage} />}</Route>
        <Route path="/ap/bills">{() => <Redirect to="/ap?tab=bills" />}</Route>
        <Route path="/ap/vendors">{() => <Redirect to="/ap?tab=vendors" />}</Route>
        <Route path="/ap/purchase-orders">{() => <Redirect to="/ap?tab=purchase-orders" />}</Route>
        <Route path="/ap/grns">{() => <Redirect to="/ap?tab=goods-received" />}</Route>
        <Route path="/ar">{() => <GatedRoute feature="ar" Component={ARPage} />}</Route>
        <Route path="/ar/customers/new">{() => <GatedRoute feature="ar" Component={CustomerFormPage} />}</Route>
        <Route path="/ar/invoices/new/manual">{() => <GatedRoute feature="ar" Component={InvoiceFormPage} />}</Route>
        <Route path="/ar/invoices/new/csv">{() => <GatedRoute feature="ar" Component={InvoiceCsvUploadPage} />}</Route>
        <Route path="/ar/invoices/new/einvoice-xlsx">{() => <GatedRoute feature="ar" Component={EinvoiceBulkUploadPage} />}</Route>
        <Route path="/ar/invoices/new/sync">{() => <GatedRoute feature="ar" Component={InvoiceSyncPage} />}</Route>
        <Route path="/ar/invoices/bulk-convert">{() => <GatedRoute feature="ar" Component={InvoiceBulkConvertPage} />}</Route>
        <Route path="/ar/invoices/new">{() => <GatedRoute feature="ar" Component={InvoiceNewPage} />}</Route>
        <Route path="/ar/invoices/:id/edit">{(params) => <GatedRoute feature="ar" Component={InvoiceFormPage} params={params} />}</Route>
        <Route path="/ar/customers/:id">{(params) => <GatedRoute feature="ar" Component={CustomerDetailPage} params={params} />}</Route>
        <Route path="/ar/invoices/:id">{(params) => <GatedRoute feature="ar" Component={InvoiceDetailPage} params={params} />}</Route>
        <Route path="/ar/deals/:id">{(params) => <GatedRoute feature="deals" Component={DealDetailPage} params={params} />}</Route>
        {/* Compliance Dashboard was retired in Task #1200. Per-submission
            audit detail moved to the invoice detail's `EInvoiceTransmissionPanel`,
            and the workspace-wide success-rate KPI moved inline to the AR →
            E-Invoicing tab. Both legacy routes redirect to the new tab while
            preserving any existing query string so bookmarks keep working. */}
        <Route path="/ar/compliance">{() => {
          const search = typeof window !== "undefined" ? window.location.search : "";
          const params = new URLSearchParams(search);
          params.set("tab", "einvoicing");
          return <Redirect to={`/ar?${params.toString()}`} />;
        }}</Route>
        <Route path="/ar/compliance-dashboard">{() => {
          const search = typeof window !== "undefined" ? window.location.search : "";
          const params = new URLSearchParams(search);
          params.set("tab", "einvoicing");
          return <Redirect to={`/ar?${params.toString()}`} />;
        }}</Route>
        <Route path="/ar/credit-notes">{() => <GatedRoute feature="arInstruments" Component={CreditNotesPage} />}</Route>
        <Route path="/ar/contracts/records/:id">{(params) => <GatedRoute feature="contractInvoicing" Component={ContractRecordDetailPage} params={params} />}</Route>
        <Route path="/ar/contracts/records">{() => <Redirect to="/ar?tab=contracts" />}</Route>
        <Route path="/ar/contracts/:id">{(params) => <GatedRoute feature="contractToCash" Component={ContractDetailPage} params={params} />}</Route>
        <Route path="/ar/invoice-drafts">{() => <GatedRoute feature="contractToCash" Component={InvoiceDraftQueuePage} />}</Route>
        <Route path="/ar/tax-rates">{() => <Redirect to="/settings?section=business&sub=taxRates" />}</Route>
        <Route path="/ar/contracts">{() => <Redirect to="/ar?tab=contracts" />}</Route>
        <Route path="/ar/invoices">{() => {
          const search = typeof window !== "undefined" ? window.location.search : "";
          const params = new URLSearchParams(search);
          const fta = params.get("fta");
          if (fta) {
            params.delete("fta");
            params.set("tab", "einvoicing");
            params.set("status", fta);
            return <Redirect to={`/ar?${params.toString()}`} />;
          }
          params.set("tab", "invoices");
          return <Redirect to={`/ar?${params.toString()}`} />;
        }}</Route>
        <Route path="/ar/customers">{() => <Redirect to="/ar?tab=customers" />}</Route>
        <Route path="/ar/deals">{() => <Redirect to="/ar?tab=deals" />}</Route>
        <Route path="/financing">{() => <GatedRoute feature="financing" Component={FinancingPage} />}</Route>
        <Route path="/financing/applications/:id">{(params) => <GatedRoute feature="financing" Component={ApplicationDetailPage} params={params} />}</Route>
        <Route path="/financing/facilities/:id">{(params) => <GatedRoute feature="financing" Component={FacilityDetailPage} params={params} />}</Route>
        <Route path="/financing/applications">{() => <Redirect to="/financing?tab=applications" />}</Route>
        <Route path="/financing/facilities">{() => <Redirect to="/financing?tab=facilities" />}</Route>
        <Route path="/financing/kyb">{() => <Redirect to="/financing?tab=kyb" />}</Route>
        <Route path="/operations" component={OperationsPage} />
        <Route path="/reconciliation">{() => <Redirect to="/operations?tab=reconciliation" />}</Route>
        <Route path="/inbox" component={InboxPage} />
        <Route path="/automations/approvals">{() => <GatedRoute feature="automations" Component={AutomationApprovalsPage} />}</Route>
        <Route path="/automations/runs">{() => <GatedRoute feature="automations" Component={AutomationRunsPage} />}</Route>
        <Route path="/automations/health">{() => <GatedRoute feature="automations" Component={AutomationHealthPage} />}</Route>
        <Route path="/automations/:id">{(params) => <GatedRoute feature="automations" Component={AutomationBuilderPage} params={params} />}</Route>
        <Route path="/automations">{() => <GatedRoute feature="automations" Component={AutomationsListPage} />}</Route>
        <Route path="/pipeline/instances/:id/setup">{(params) => <GatedRoute feature="automations" Component={AlBillingWizardPage} params={params} />}</Route>
        <Route path="/pipeline/instances/:id">{(params) => <GatedRoute feature="automations" Component={PipelineInstanceDetailPage} params={params} />}</Route>
        <Route path="/pipeline/instances">{() => <GatedRoute feature="automations" Component={PipelineInstancesPage} />}</Route>
        <Route path="/pipeline">{() => <GatedRoute feature="automations" Component={PipelineInstancesPage} />}</Route>
        <Route path="/tasks/new">{() => <GatedRoute feature="tasks" Component={TaskFormPage} />}</Route>
        <Route path="/reminders" component={RemindersRedirect} />
        <Route path="/tasks">{() => <GatedRoute feature="tasks" Component={TasksPage} />}</Route>
        <Route path="/reports">{() => <GatedRoute feature="aiReports" Component={ReportsPage} />}</Route>
        <Route path="/reports/budgets">{() => <GatedRoute feature="aiReports" Component={BudgetsPage} />}</Route>
        <Route path="/reports/budget-vs-actual">{() => <GatedRoute feature="aiReports" Component={BudgetVsActualPage} />}</Route>
        <Route path="/reports/schedules">{() => <GatedRoute feature="aiReports" Component={SchedulesPage} />}</Route>
        <Route path="/reports/builder">{() => <GatedRoute feature="aiReports" Component={ReportBuilderPage} />}</Route>
        <Route path="/reports/:slug">{(params) => <GatedRoute feature="aiReports" Component={ReportDetailPage} params={params} />}</Route>
        <Route path="/settings" component={SettingsPage} />
        <Route path="/system-health" component={SystemHealthPage} />
        <Route path="/choose-plan" component={ChoosePlanPage} />
        <Route path="/sync" component={SyncRedirect} />
        <Route path="/communications">{() => <GatedRoute feature="communications" Component={CommunicationsPage} />}</Route>
        <Route path="/chat">{() => <GatedRoute feature="aiChat" Component={ChatPage} />}</Route>
        <Route path="/admin" component={AdminPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AuthenticatedApp() {
  return (
    <SupportModeProvider>
      <EntityProvider>
        <FeaturesProvider>
          <SyncActivityProvider>
          <UpgradeModalProvider>
            <div className="h-screen flex flex-col bg-background text-foreground overflow-x-hidden">
              <SupportModeBanner />
              <TrialBanner />
              <SyncBanner />
              <HeaderNav />
              <main className="flex-1 overflow-auto overflow-x-hidden min-h-0 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
                <AppRouter />
              </main>
              <BottomNav />
              <CommandPalette />
            </div>
            <UpgradeModal />
          </UpgradeModalProvider>
          </SyncActivityProvider>
        </FeaturesProvider>
      </EntityProvider>
    </SupportModeProvider>
  );
}

function OnboardingGate() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { data: workspace, isLoading: wsLoading } = useQuery<Workspace>({ queryKey: ["/api/workspace"] });
  const { data: featuresData, isLoading: featLoading } = useQuery<import("@/lib/features").FeatureFlags>({ queryKey: ["/api/workspace/features"] });
  const { data: billingData } = useQuery<{ trialEndsAt: string | null }>({ queryKey: ["/api/billing/status"] });

  const inSupportMode = !!user?.supportMode;
  const planChosen = featuresData?.planChosen ?? false;
  const upgradeOnboardingTier = featuresData?.upgradeOnboardingTier;
  const downgradeNotice = featuresData?.downgradeNotice;

  const sessionTrackedRef = useRef(false);
  useEffect(() => {
    if (sessionTrackedRef.current || !featuresData || !user || wsLoading || featLoading) return;
    sessionTrackedRef.current = true;
    resetSessionModules();
    registerSuperProperties({ tier: featuresData.tier });
    try { localStorage.setItem("octa-cached-tier", featuresData.tier); } catch {}

    const signupKey = wsLocalKey("octa-signup-completed");
    if (user.firstLoginPending && !localStorage.getItem(signupKey)) {
      try { localStorage.setItem(signupKey, "1"); } catch {}
      const utms = captureAndRegisterUtms();
      setUserProfileOnce({
        $created: new Date().toISOString(),
        signup_date: new Date().toISOString(),
      });
      // GA4 recommended conversion: signup_completed
      trackEvent("Signup Completed", {
        signup_method: "auth0",
        referrer: document.referrer || undefined,
        ...utms,
      });
    } else if (!user.firstLoginPending) {
      incrementUserProfile("login_count");
      const loginKey = wsLocalKey("octa-login-count");
      const loginCountRaw = localStorage.getItem(loginKey);
      const loginCount = loginCountRaw ? parseInt(loginCountRaw, 10) + 1 : 1;
      try { localStorage.setItem(loginKey, String(loginCount)); } catch {}
      trackEvent("Login", { login_count: loginCount });
      // GTM dataLayer: emit a `login` event so GTM-managed conversion tags
      // (Facebook Pixel, LinkedIn Insight, etc.) can fire on returning logins.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "login", user_id: user.id });
    }

    incrementUserProfile("session_count");
    const sessionKey = wsLocalKey("octa-session-count");
    const sessionCountRaw = localStorage.getItem(sessionKey);
    const sessionNumber = sessionCountRaw ? parseInt(sessionCountRaw, 10) + 1 : 1;
    try { localStorage.setItem(sessionKey, String(sessionNumber)); } catch {}
    trackEvent("Session Started", { session_number: sessionNumber });
    registerSuperProperties({ last_active_date: new Date().toISOString() });
    setUserProfileProperties({
      current_tier: featuresData.tier,
      subscription_status: featuresData.subscriptionStatus || "unknown",
    });

    const prevStatus = localStorage.getItem(wsLocalKey("octa-prev-subscription-status"));
    const prevTier = localStorage.getItem(wsLocalKey("octa-prev-tier"));
    if (featuresData.subscriptionStatus === "active" && prevStatus && prevStatus !== "active") {
      trackEvent("Subscription Activated", { tier: featuresData.tier });
    }
    if (prevStatus === "trialing" && featuresData.tier === "starter" && featuresData.subscriptionStatus !== "trialing") {
      trackEvent("Trial Expired", { previous_tier: prevTier || "growth" });
    }
    try {
      localStorage.setItem(wsLocalKey("octa-prev-subscription-status"), featuresData.subscriptionStatus || "");
      localStorage.setItem(wsLocalKey("octa-prev-tier"), featuresData.tier || "");
    } catch {}
  }, [featuresData, user, wsLoading, featLoading]);

  useEffect(() => {
    if (!billingData?.trialEndsAt) return;
    setUserProfileProperties({ trial_end_date: billingData.trialEndsAt });
  }, [billingData?.trialEndsAt]);

  useEffect(() => {
    if (inSupportMode) return;
    if (wsLoading || featLoading) return;
    if (workspace?.locale && workspace.locale !== i18n.language) {
      i18n.changeLanguage(workspace.locale);
    }

    if (!planChosen) return;

    if (downgradeNotice && workspace?.onboardingCompleted) {
      if (location !== "/downgrade-notice") {
        setLocation("/downgrade-notice");
      }
      return;
    }

    if (upgradeOnboardingTier && workspace?.onboardingCompleted) {
      if (location !== "/upgrade-welcome") {
        setLocation("/upgrade-welcome");
      }
      return;
    }

    if (workspace && !workspace.onboardingCompleted) {
      if (location !== "/onboarding") {
        setLocation("/onboarding");
      }
    } else if (workspace && workspace.onboardingCompleted) {
      if (location === "/onboarding" || location === "/upgrade-welcome" || location === "/downgrade-notice") {
        setLocation("/");
      }
    }
  }, [workspace, wsLoading, featLoading, planChosen, upgradeOnboardingTier, downgradeNotice, location, setLocation, inSupportMode]);

  if (inSupportMode) {
    return <AuthenticatedApp />;
  }

  if (wsLoading || featLoading) {
    return <FullScreenLoader />;
  }

  if (!planChosen) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <FeaturesProvider>
          <ChoosePlanPage />
        </FeaturesProvider>
      </Suspense>
    );
  }

  if (downgradeNotice && workspace?.onboardingCompleted) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <FeaturesProvider>
          <DowngradeNoticePage downgradeNotice={downgradeNotice} />
        </FeaturesProvider>
      </Suspense>
    );
  }

  if (upgradeOnboardingTier && workspace?.onboardingCompleted) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <FeaturesProvider>
          <UpgradeWelcomePage upgradeTier={upgradeOnboardingTier} />
        </FeaturesProvider>
      </Suspense>
    );
  }

  if (workspace && !workspace.onboardingCompleted) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <FeaturesProvider>
          <OnboardingPage />
        </FeaturesProvider>
      </Suspense>
    );
  }

  return <AuthenticatedApp />;
}

function SuperAdminShell() {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (location !== "/admin") {
      setLocation("/admin", { replace: true });
    }
  }, [location, setLocation]);

  return (
    <SupportModeProvider>
      <EntityProvider>
        <FeaturesProvider>
          <div className="h-screen flex flex-col bg-background text-foreground">
            <HeaderNav />
            <main className="flex-1 overflow-auto min-h-0 pb-16 md:pb-0">
              <Suspense fallback={<PageLoader />}>
                <AdminPage />
              </Suspense>
            </main>
            <BottomNav />
          </div>
        </FeaturesProvider>
      </EntityProvider>
    </SupportModeProvider>
  );
}

function AppContent() {
  const { user, isLoading } = useAuth();

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.fullName,
      });
      Sentry.setTag("workspaceId", user.workspaceId);
    } else if (!isLoading) {
      Sentry.setUser(null);
      Sentry.setTag("workspaceId", undefined);
    }
  }, [user, isLoading]);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <AuthPage />
      </Suspense>
    );
  }

  if (user.role === "superadmin" && !user.supportMode) {
    return <SuperAdminShell />;
  }

  return <OnboardingGate />;
}

function App() {
  usePageTracking();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Suspense fallback={<FullScreenLoader />}>
            <Switch>
              <Route path="/pay/:id" component={PayRedirect} />
              <Route path="/portal/:customerId" component={CustomerPortalPage} />
              <Route path="/vendor-portal/:vendorId" component={VendorPortalPage} />
              <Route path="/checkout" component={CheckoutRedirect} />
              <Route path="/activate" component={ActivatePage} />
              <Route>
                <AuthProvider>
                  <AppContent />
                </AuthProvider>
              </Route>
            </Switch>
          </Suspense>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
